import { useState } from 'react';
import type { AdRewardConfig, AdRewardCopyConfig, PinAcquireMethod } from '../lib/adReward';
import { defaultAdRewardCopy } from '../lib/adReward';

type Props = {
  cfg: AdRewardConfig;
  onChange: (patch: Partial<AdRewardConfig> | AdRewardConfig) => void;
  /** 上传客服置顶二维码，返回 media path */
  onUploadServiceQr?: (file: File) => Promise<string>;
  resolveMediaUrl?: (path: string) => string;
};

function applyPinMethod(
  method: PinAcquireMethod,
  current: AdRewardConfig,
  extra: Partial<AdRewardConfig> = {},
): AdRewardConfig {
  const next: AdRewardConfig = {
    ...current,
    ...extra,
    mode: 'pin',
    pin_method: method,
    refresh_owner_enabled: false,
    refresh_other_enabled: false,
  };
  if (method === 'share' || method === 'service') {
    next.pin_owner_enabled = true;
    next.pin_other_enabled = false;
  } else if (method === 'ad') {
    // 自己置顶 + 他人帮置顶都关 = 关闭用户侧置顶（勿强行勾回，否则勾选框点不动）
    if (!next.pin_owner_enabled && !next.pin_other_enabled) {
      return clearPin(next);
    }
  }
  return next;
}

function applyRefreshGroup(patch: Partial<AdRewardConfig>, current: AdRewardConfig): AdRewardConfig {
  return {
    ...current,
    ...patch,
    mode: 'refresh',
    pin_method: '',
    pin_owner_enabled: false,
    pin_other_enabled: false,
  };
}

function clearPin(current: AdRewardConfig): AdRewardConfig {
  return {
    ...current,
    mode: current.refresh_owner_enabled || current.refresh_other_enabled ? 'refresh' : '',
    pin_method: '',
    pin_owner_enabled: false,
    pin_other_enabled: false,
  };
}

function patchCopy(cfg: AdRewardConfig, copyPatch: Partial<AdRewardCopyConfig>): AdRewardConfig {
  return { ...cfg, copy: { ...cfg.copy, ...copyPatch } };
}

function CopyField({
  label,
  hint,
  value,
  disabled,
  multiline,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  disabled?: boolean;
  multiline?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-sm text-slate-700">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {hint ? <span className="mb-1 block text-[11px] text-slate-400">{hint}</span> : null}
      {multiline ? (
        <textarea
          rows={3}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

const METHOD_OPTIONS: { value: PinAcquireMethod | 'off'; label: string; desc: string }[] = [
  { value: 'ad', label: '激励广告置顶', desc: '完整观看激励视频后置顶，可产生流量主收益' },
  { value: 'share', label: '转发送置顶', desc: '发布人转发后，按打开人数达标置顶，利于拉新扩量' },
  { value: 'service', label: '联系客服置顶', desc: '展示客服微信二维码，由运营人工置顶' },
  { value: 'off', label: '关闭置顶', desc: '不提供用户侧置顶入口（仍可用后台手动置顶）' },
];

export function MiniprogramAdRewardSettingsCard({
  cfg,
  onChange,
  onUploadServiceQr,
  resolveMediaUrl,
}: Props) {
  const pinActive = cfg.mode === 'pin';
  const refreshActive = cfg.mode === 'refresh';
  const pinMethod: PinAcquireMethod | 'off' = pinActive
    ? cfg.pin_method === 'share' || cfg.pin_method === 'service' || cfg.pin_method === 'ad'
      ? cfg.pin_method
      : 'ad'
    : 'off';
  const [copyOpen, setCopyOpen] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const copy = cfg.copy ?? defaultAdRewardCopy();

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-slate-800">A 组：信息置顶（三种获取方式）</h4>
        <p className="mt-1 text-xs text-slate-500">
          与 B 组互斥。可选激励广告、转发送置顶或联系客服；信息类型可单独覆盖本总配置。
        </p>
        <div className={`mt-3 grid gap-2 sm:grid-cols-2 ${refreshActive ? 'pointer-events-none opacity-45' : ''}`}>
          {METHOD_OPTIONS.map((opt) => {
            const checked = pinMethod === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border bg-white p-3 ${
                  checked ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-100'
                }`}
              >
                <input
                  type="radio"
                  className="mt-1 h-4 w-4"
                  name="pin_acquire_method"
                  checked={checked}
                  disabled={refreshActive}
                  onChange={() => {
                    if (opt.value === 'off') {
                      onChange(clearPin(cfg));
                      return;
                    }
                    onChange(applyPinMethod(opt.value, cfg));
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-800">{opt.label}</span>
                  <span className="mt-1 block text-xs text-slate-500">{opt.desc}</span>
                </span>
              </label>
            );
          })}
        </div>

        {pinActive && pinMethod === 'ad' ? (
          <div className={`mt-4 space-y-3 ${refreshActive ? 'pointer-events-none opacity-45' : ''}`}>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!cfg.pin_owner_enabled}
                disabled={refreshActive}
                onChange={(e) => {
                  const on = e.target.checked;
                  onChange(
                    applyPinMethod('ad', cfg, {
                      pin_owner_enabled: on,
                      pin_other_enabled: on ? cfg.pin_other_enabled : cfg.pin_other_enabled,
                    }),
                  );
                }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">发布人自己看广告置顶</span>
                <span className="mt-1 block text-xs text-slate-500">
                  发布成功后引导；详情页可继续叠加天数。若「他人帮置顶」也未开启，取消本项将等同于关闭置顶。
                </span>
              </span>
            </label>
            {cfg.pin_owner_enabled ? (
              <div className="space-y-3 pl-7">
                <label className="block text-sm text-slate-700">
                  <span className="mb-1 block text-xs font-medium text-slate-600">赠送置顶天数（每次完整观看，可叠加）</span>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={cfg.pin_reward_days ?? 3}
                    disabled={refreshActive}
                    onChange={(e) =>
                      onChange(
                        applyPinMethod('ad', cfg, {
                          pin_reward_days: Math.min(30, Math.max(1, Number(e.target.value) || 3)),
                        }),
                      )
                    }
                  />
                </label>
                <label className="block text-sm text-slate-700">
                  <span className="mb-1 block text-xs font-medium text-slate-600">发布人每日自己置顶次数（0 表示不限）</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={cfg.pin_owner_daily_limit ?? 3}
                    disabled={refreshActive}
                    onChange={(e) =>
                      onChange(
                        applyPinMethod('ad', cfg, {
                          pin_owner_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                        }),
                      )
                    }
                  />
                </label>
              </div>
            ) : null}
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!cfg.pin_other_enabled}
                disabled={refreshActive}
                onChange={(e) => onChange(applyPinMethod('ad', cfg, { pin_other_enabled: e.target.checked }))}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">非发布人看广告帮 TA 置顶</span>
                <span className="mt-1 block text-xs text-slate-500">亲友打开详情后可助力置顶。</span>
              </span>
            </label>
            {cfg.pin_other_enabled ? (
              <label className="block text-sm text-slate-700 pl-7">
                <span className="mb-1 block text-xs font-medium text-slate-600">每人每日帮他人置顶次数（0 表示不限）</span>
                <input
                  type="number"
                  min={0}
                  max={99}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.pin_other_daily_limit ?? 5}
                  disabled={refreshActive}
                  onChange={(e) =>
                    onChange(
                      applyPinMethod('ad', cfg, {
                        pin_other_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                      }),
                    )
                  }
                />
              </label>
            ) : null}
          </div>
        ) : null}

        {pinActive && pinMethod === 'share' ? (
          <div className={`mt-4 space-y-3 rounded-lg border border-emerald-100 bg-white p-3 ${refreshActive ? 'opacity-45' : ''}`}>
            <p className="text-xs text-slate-500">
              仅统计「通过发布人转发卡片」打开详情的去重用户；同一人终身只计 1 次。达标后开启下一轮，需其他未参与过的人打开才能再叠加置顶。
            </p>
            <label className="block text-sm text-slate-700">
              <span className="mb-1 block text-xs font-medium text-slate-600">达标所需打开人数</span>
              <input
                type="number"
                min={1}
                max={999}
                className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.share_open_required ?? 10}
                disabled={refreshActive}
                onChange={(e) =>
                  onChange(
                    applyPinMethod('share', cfg, {
                      share_open_required: Math.min(999, Math.max(1, Number(e.target.value) || 10)),
                    }),
                  )
                }
              />
            </label>
            <label className="block text-sm text-slate-700">
              <span className="mb-1 block text-xs font-medium text-slate-600">每次达标赠送置顶天数</span>
              <input
                type="number"
                min={1}
                max={30}
                className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.share_pin_days ?? 3}
                disabled={refreshActive}
                onChange={(e) =>
                  onChange(
                    applyPinMethod('share', cfg, {
                      share_pin_days: Math.min(30, Math.max(1, Number(e.target.value) || 3)),
                    }),
                  )
                }
              />
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!cfg.share_require_new_user}
                disabled={refreshActive}
                onChange={(e) =>
                  onChange(applyPinMethod('share', cfg, { share_require_new_user: e.target.checked }))
                }
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">仅统计平台新用户打开</span>
                <span className="mt-1 block text-xs text-slate-500">
                  勾选后：只有首次进入本小程序的新用户打开才计入进度；不勾选则老用户打开也计入。
                </span>
              </span>
            </label>
          </div>
        ) : null}

        {pinActive && pinMethod === 'service' ? (
          <div className={`mt-4 space-y-3 rounded-lg border border-sky-100 bg-white p-3 ${refreshActive ? 'opacity-45' : ''}`}>
            <label className="block text-sm text-slate-700">
              <span className="mb-1 block text-xs font-medium text-slate-600">引导标题</span>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.service_title ?? ''}
                disabled={refreshActive}
                onChange={(e) => onChange(applyPinMethod('service', cfg, { service_title: e.target.value }))}
              />
            </label>
            <label className="block text-sm text-slate-700">
              <span className="mb-1 block text-xs font-medium text-slate-600">引导说明</span>
              <textarea
                rows={3}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.service_body ?? ''}
                disabled={refreshActive}
                onChange={(e) => onChange(applyPinMethod('service', cfg, { service_body: e.target.value }))}
              />
            </label>
            <div>
              <span className="mb-1 block text-xs font-medium text-slate-600">客服微信二维码</span>
              <div className="flex flex-wrap items-center gap-3">
                {cfg.service_qr_path && resolveMediaUrl ? (
                  <img
                    src={resolveMediaUrl(cfg.service_qr_path)}
                    alt="客服二维码"
                    className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-[11px] text-slate-400">
                    未上传
                  </div>
                )}
                <div className="space-y-2">
                  <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                    {uploadingQr ? '上传中…' : '上传二维码'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={refreshActive || uploadingQr || !onUploadServiceQr}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f || !onUploadServiceQr) return;
                        setUploadingQr(true);
                        void onUploadServiceQr(f)
                          .then((path) => {
                            onChange(applyPinMethod('service', cfg, { service_qr_path: path }));
                          })
                          .finally(() => setUploadingQr(false));
                      }}
                    />
                  </label>
                  {cfg.service_qr_path ? (
                    <button
                      type="button"
                      className="block text-xs text-rose-600 hover:underline"
                      disabled={refreshActive}
                      onClick={() => onChange(applyPinMethod('service', cfg, { service_qr_path: '' }))}
                    >
                      清除二维码
                    </button>
                  ) : null}
                </div>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">用户长按识别后添加客服，发送信息编号；运营在后台手动置顶。</p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="border-t border-slate-200/80 pt-5">
        <h4 className="text-sm font-semibold text-slate-800">B 组：激励视频送信息刷新</h4>
        <p className="mt-1 text-xs text-slate-500">刷新后按列表「刷新时间」排序靠前；与 A 组互斥。</p>
        <div className={`mt-3 space-y-3 ${pinActive ? 'pointer-events-none opacity-45' : ''}`}>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!cfg.refresh_owner_enabled}
              disabled={pinActive}
              onChange={(e) => onChange(applyRefreshGroup({ refresh_owner_enabled: e.target.checked }, cfg))}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">开启：发布人自己看激励广告送信息刷新</span>
              <span className="mt-1 block text-xs text-slate-500">发布超过设定时长后可自动引导刷新。</span>
            </span>
          </label>
          {cfg.refresh_owner_enabled ? (
            <div className="space-y-3 pl-7">
              <label className="block text-sm text-slate-700">
                <span className="mb-1 block text-xs font-medium text-slate-600">自动引导刷新（发布后满小时数）</span>
                <input
                  type="number"
                  min={1}
                  max={168}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.refresh_owner_auto_hours ?? 24}
                  disabled={pinActive}
                  onChange={(e) =>
                    onChange(
                      applyRefreshGroup(
                        {
                          refresh_owner_auto_hours: Math.min(168, Math.max(1, Number(e.target.value) || 24)),
                        },
                        cfg,
                      ),
                    )
                  }
                />
              </label>
              <label className="block text-sm text-slate-700">
                <span className="mb-1 block text-xs font-medium text-slate-600">发布人每日自己刷新次数（0 表示不限）</span>
                <input
                  type="number"
                  min={0}
                  max={99}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.refresh_owner_daily_limit ?? 3}
                  disabled={pinActive}
                  onChange={(e) =>
                    onChange(
                      applyRefreshGroup(
                        {
                          refresh_owner_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                        },
                        cfg,
                      ),
                    )
                  }
                />
              </label>
            </div>
          ) : null}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!cfg.refresh_other_enabled}
              disabled={pinActive}
              onChange={(e) => onChange(applyRefreshGroup({ refresh_other_enabled: e.target.checked }, cfg))}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">开启：非发布人看激励广告送信息刷新</span>
            </span>
          </label>
          {cfg.refresh_other_enabled ? (
            <label className="block text-sm text-slate-700 pl-7">
              <span className="mb-1 block text-xs font-medium text-slate-600">每人每日帮他人刷新次数（0 表示不限）</span>
              <input
                type="number"
                min={0}
                max={99}
                className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.refresh_other_daily_limit ?? 5}
                disabled={pinActive}
                onChange={(e) =>
                  onChange(
                    applyRefreshGroup(
                      {
                        refresh_other_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                      },
                      cfg,
                    ),
                  )
                }
              />
            </label>
          ) : null}
        </div>
      </div>

      {(pinActive || refreshActive) && (
        <>
          <div className="border-t border-slate-200/80 pt-5">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
              onClick={() => setCopyOpen((v) => !v)}
            >
              <span>弹窗文案配置（可自定义）</span>
              <span className="text-slate-400">{copyOpen ? '收起' : '展开'}</span>
            </button>
            {copyOpen ? (
              <div className="mt-3 space-y-4 rounded-lg border border-slate-100 bg-white p-3">
                <p className="text-[11px] text-slate-400">
                  变量：{'{days}'} {'{hours}'} {'{remaining}'} {'{limit}'} {'{required}'} {'{current}'}
                </p>
                {pinActive && pinMethod === 'ad' ? (
                  <div className="space-y-3">
                    <h5 className="text-xs font-semibold text-amber-800">广告置顶 · 发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.pin_owner_title} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_title: v }))} />
                    <CopyField label="引导弹窗内容" hint="支持 {days}" multiline value={copy.pin_owner_body} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_body: v }))} />
                    <CopyField label="成功提示" hint="支持 {days}" value={copy.pin_owner_success} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_success: v }))} />
                    <h5 className="pt-2 text-xs font-semibold text-amber-800">广告置顶 · 非发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.pin_other_title} onChange={(v) => onChange(patchCopy(cfg, { pin_other_title: v }))} />
                    <CopyField label="引导弹窗内容" multiline value={copy.pin_other_body} onChange={(v) => onChange(patchCopy(cfg, { pin_other_body: v }))} />
                  </div>
                ) : null}
                {pinActive && pinMethod === 'share' ? (
                  <div className="space-y-3">
                    <h5 className="text-xs font-semibold text-emerald-800">转发送置顶</h5>
                    <CopyField label="标题" value={copy.share_owner_title} onChange={(v) => onChange(patchCopy(cfg, { share_owner_title: v }))} />
                    <CopyField label="说明" hint="支持 {required}{current}{days}{remaining}" multiline value={copy.share_owner_body} onChange={(v) => onChange(patchCopy(cfg, { share_owner_body: v }))} />
                    <CopyField label="详情页按钮文案" value={copy.share_owner_btn} onChange={(v) => onChange(patchCopy(cfg, { share_owner_btn: v }))} />
                    <CopyField label="弹窗主按钮" value={copy.share_modal_btn} onChange={(v) => onChange(patchCopy(cfg, { share_modal_btn: v }))} />
                    <CopyField label="进度文案" value={copy.share_owner_progress} onChange={(v) => onChange(patchCopy(cfg, { share_owner_progress: v }))} />
                    <CopyField
                      label="达标后下一轮文案"
                      hint="本轮达标清空后显示；支持 {grants}{current}{required}{days}"
                      value={copy.share_owner_round_done}
                      onChange={(v) => onChange(patchCopy(cfg, { share_owner_round_done: v }))}
                    />
                    <CopyField label="达标成功提示" value={copy.share_owner_success} onChange={(v) => onChange(patchCopy(cfg, { share_owner_success: v }))} />
                  </div>
                ) : null}
                {pinActive && pinMethod === 'service' ? (
                  <div className="space-y-3">
                    <h5 className="text-xs font-semibold text-sky-800">客服置顶文案（可与上方字段一致）</h5>
                    <CopyField label="按钮文案" value={copy.service_owner_btn} onChange={(v) => onChange(patchCopy(cfg, { service_owner_btn: v }))} />
                    <CopyField label="弹窗标题" value={copy.service_owner_title} onChange={(v) => onChange(patchCopy(cfg, { service_owner_title: v }))} />
                    <CopyField label="弹窗说明" multiline value={copy.service_owner_body} onChange={(v) => onChange(patchCopy(cfg, { service_owner_body: v }))} />
                  </div>
                ) : null}
                {refreshActive ? (
                  <div className="space-y-3">
                    <h5 className="text-xs font-semibold text-teal-800">刷新 · 发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.refresh_owner_title} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_title: v }))} />
                    <CopyField label="引导弹窗内容" multiline value={copy.refresh_owner_body} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_body: v }))} />
                    <CopyField label="自动引导弹窗内容" hint="支持 {hours}" multiline value={copy.refresh_owner_auto_body} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_auto_body: v }))} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {pinMethod === 'ad' || refreshActive ? (
            <p className="rounded-lg border border-amber-200/90 bg-amber-50/60 px-3 py-2 text-xs text-amber-950">
              使用激励视频时，请确认已在「运营扩展 → 流量主广告」中开启激励视频并填写广告位 ID。
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
